// src/components/auth/signin-form.tsx
'use client';

import { useState, type FormEvent, useEffect } from 'react'; // Added useEffect
import Link from 'next/link';
import { useRouter, useParams, useSearchParams } from 'next/navigation'; // Added useSearchParams
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, LogIn, Loader2, Briefcase, Info } from 'lucide-react'; // Added Info
import { auth } from '@/lib/firebase';
import { signInWithEmailAndPassword as firebaseSignIn, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import type { FirebaseError } from 'firebase/app';
import { z } from 'zod';
import { createSessionCookieAction } from '@/lib/actions/auth-session-actions';
import { useToast } from "@/hooks/use-toast";
import type { Dictionary } from '@/lib/i18n';
import { GoogleSignInButton } from './google-signin-button';
import { MagicLinkForm } from './magic-link-form';
import { signInWithEmailAndPassword } from '@/lib/actions/auth-actions';

const SignInSchemaClient = z.object({
  email: z.string().email({ message: "Invalid email address." }),
  password: z.string().min(6, { message: "Password must be at least 6 characters." }),
});

type SignInFormClientState = {
  errors?: {
    email?: string[];
    password?: string[];
    generic?: string[];
  };
  message?: string | null;
  isLoading: boolean;
};

export function SignInForm({ dictionary }: { dictionary: Dictionary['auth']['signIn'] }) {
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams(); // Get search params
  const lang = params.lang || 'en-US';
  const { toast } = useToast();
  const [formState, setFormState] = useState<SignInFormClientState>({ isLoading: false });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isProcessingMagicLink, setIsProcessingMagicLink] = useState(true);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);

  // Effect to handle post-registration messages and magic links
  useEffect(() => {
    // Check for verification message from sign-up
    const status = searchParams.get('status');
    if (status === 'verification-sent' && dictionary.verificationSent) {
      setVerificationMessage(dictionary.verificationSent);
    }

    const completeMagicLinkSignIn = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        let emailFromStorage = window.localStorage.getItem('emailForSignIn');
        if (!emailFromStorage) {
          emailFromStorage = window.prompt(dictionary.magicLinkComplete.emailMissing);
        }

        if (!emailFromStorage) {
           toast({
            title: dictionary.magicLinkComplete.errorTitle,
            description: "Email address is required to complete sign-in.",
            variant: "destructive",
          });
          setIsProcessingMagicLink(false);
          return;
        }

        toast({
            title: dictionary.magicLinkComplete.progressTitle,
            description: dictionary.magicLinkComplete.progressDescription,
        });

        try {
          const userCredential = await signInWithEmailLink(auth, emailFromStorage, window.location.href);
          window.localStorage.removeItem('emailForSignIn');

          const idToken = await userCredential.user.getIdToken();
          const sessionResult = await createSessionCookieAction(idToken);

          if (sessionResult.success) {
            toast({ title: "Login Successful", description: "Redirecting..." });
            router.push(sessionResult.loginRedirectLink || `/${lang}/dashboard`);
          } else {
            throw new Error(sessionResult.error || "Server session creation failed.");
          }
        } catch (error) {
          console.error("Magic Link Sign-In Error:", error);
          toast({
            title: dictionary.magicLinkComplete.errorTitle,
            description: dictionary.magicLinkComplete.invalidLink,
            variant: "destructive",
          });
          router.replace(`/${lang}/signin`);
        } finally {
            setIsProcessingMagicLink(false);
        }
      } else {
          setIsProcessingMagicLink(false);
      }
    };
    
    completeMagicLinkSignIn();
  }, [router, toast, lang, dictionary, searchParams]);


  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    console.log("[SignInForm] INFO: Handle submit initiated.");
    setFormState({ isLoading: true, errors: {}, message: null });

    const validatedFields = SignInSchemaClient.safeParse({ email, password });

    if (!validatedFields.success) {
      console.warn("[SignInForm] WARN: Client-side validation failed.", validatedFields.error.flatten().fieldErrors);
      setFormState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: 'Invalid fields. Sign in failed.',
        isLoading: false,
      });
      return;
    }
    
    // Call server action first to check verification status
    const verificationResult = await signInWithEmailAndPassword(validatedFields.data);
    if (!verificationResult.success) {
        setFormState({ isLoading: false, errors: { generic: [verificationResult.error!] } });
        toast({
            title: dictionary.errorTitle,
            description: verificationResult.error,
            variant: "destructive",
        });
        return;
    }

    try {
      console.log("[SignInForm] INFO: Attempting Firebase client-side sign-in for email:", validatedFields.data.email);
      const userCredential = await firebaseSignIn(auth, validatedFields.data.email, validatedFields.data.password);
      
      console.log("[SignInForm] SUCCESS: Firebase client-side sign-in successful. UID:", userCredential.user.uid);

      if (userCredential.user) {
        console.log("[SignInForm] INFO: Obtaining ID token from Firebase client SDK...");
        const idToken = await userCredential.user.getIdToken();
        console.log("[SignInForm] INFO: Obtained ID token (first 30 chars):", idToken ? idToken.substring(0, 30) + "..." : "NULL ID TOKEN");
        
        if (!idToken) {
          throw new Error("Could not retrieve ID token.");
        }
        
        console.log("[SignInForm] INFO: Calling createSessionCookieAction server action...");
        const sessionResult = await createSessionCookieAction(idToken);
        console.log("[SignInForm] INFO: Server action createSessionCookieAction RESULT:", sessionResult);

        if (sessionResult.success) {
          console.log("[SignInForm] SUCCESS: Server session cookie created successfully. UID from server:", sessionResult.uid);
          toast({ title: "Login Successful", description: "Redirecting..." });
          router.push(sessionResult.loginRedirectLink || `/${lang}/dashboard`);
        } else {
          throw new Error(sessionResult.error || 'Server session setup failed. Please try again.');
        }
      } else {
        throw new Error("Sign in failed: No user data received.");
      }
    } catch (e) {
      const error = e as FirebaseError;
      console.error('[SignInForm] ERROR: Firebase client-side Sign In Error. Code:', error.code, 'Message:', error.message);
      let genericErrorMessage = dictionary.genericError || 'Sign in failed. Please try again.';
      const fieldErrors: SignInFormClientState["errors"] = {};

      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        genericErrorMessage = dictionary.genericError;
        fieldErrors.generic = [genericErrorMessage];
      } else if (error.code === 'auth/invalid-email') {
        fieldErrors.email = ['Invalid email format.'];
      } else {
        fieldErrors.generic = [genericErrorMessage];
      }
      setFormState({
        errors: fieldErrors,
        message: null,
        isLoading: false,
      });
    }
  };

  if (isProcessingMagicLink) {
    return (
        <Card className="shadow-2xl">
            <CardHeader className="text-center">
                <CardTitle className="text-2xl">{dictionary.magicLinkComplete.progressTitle}</CardTitle>
                <CardDescription>{dictionary.magicLinkComplete.progressDescription}</CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center items-center py-8">
                 <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </CardContent>
        </Card>
    )
  }

  return (
    <Card className="shadow-2xl">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">{dictionary.title}</CardTitle>
        <CardDescription>{dictionary.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
            {verificationMessage && (
                <Alert variant="default" className="border-green-500/50 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 dark:border-green-500/60">
                    <Info className="h-4 w-4 !text-green-600 dark:!text-green-400" />
                    <AlertTitle>{dictionary.verificationTitle}</AlertTitle>
                    <AlertDescription>{verificationMessage}</AlertDescription>
                </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
                {formState.errors?.generic && (
                    <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{dictionary.errorTitle}</AlertTitle>
                    <AlertDescription>{formState.errors.generic.join(', ')}</AlertDescription>
                    </Alert>
                )}
                <div className="space-y-2">
                    <Label htmlFor="email">{dictionary.emailLabel}</Label>
                    <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="name@example.com"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-describedby="email-error"
                    disabled={formState.isLoading}
                    />
                    {formState.errors?.email && (
                    <div id="email-error" aria-live="polite" className="text-sm text-destructive">
                        {formState.errors.email.map((errorMsg: string) => (
                        <p key={errorMsg}>{errorMsg}</p>
                        ))}
                    </div>
                    )}
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="password">{dictionary.passwordLabel}</Label>
                        <Button variant="link" asChild className="p-0 h-auto text-xs">
                          <Link href={`/${lang}/forgot-password`}>
                            {dictionary.forgotPasswordLink}
                          </Link>
                        </Button>
                    </div>
                    <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-describedby="password-error"
                    disabled={formState.isLoading}
                    />
                    {formState.errors?.password && (
                    <div id="password-error" aria-live="polite" className="text-sm text-destructive">
                        {formState.errors.password.map((errorMsg: string) => (
                        <p key={errorMsg}>{errorMsg}</p>
                        ))}
                    </div>
                    )}
                </div>
                <Button type="submit" className="w-full" aria-disabled={formState.isLoading} disabled={formState.isLoading}>
                    {formState.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : (
                    <>
                        <LogIn className="mr-2 h-4 w-4" /> {dictionary.buttonText}
                    </>
                    )}
                </Button>
            </form>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">
                  {dictionary.orContinueWith}
                </span>
              </div>
            </div>
            <GoogleSignInButton />
            <div>
                <MagicLinkForm dictionary={dictionary.magicLink} />
                <p className="text-xs text-muted-foreground mt-1 px-1">{dictionary.magicLink.description}</p>
            </div>
            <div>
                 <Button variant="outline" className="w-full" asChild>
                  <Link href={`/${lang}/login/sso`}>
                    <Briefcase className="mr-2 h-4 w-4" />
                    {dictionary.sso.triggerButton}
                  </Link>
                </Button>
                 <p className="text-xs text-muted-foreground mt-1 px-1">{dictionary.sso.description}</p>
            </div>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col items-center gap-4">
        <p className="text-sm text-muted-foreground">
          {dictionary.linkText}{' '}
          <Button variant="link" asChild className="p-0 h-auto">
            <Link href={`/${lang}/signup`}>{dictionary.linkAction}</Link>
          </Button>
        </p>
      </CardFooter>
    </Card>
  );
}
